// features/settings/dashboard/DashCard.tsx — the card vocabulary shared by the
// Account Settings dashboard and the Edit Profile page (1.27.70, owner concepts
// account_settings_v2 / edit_profile_v2).
//
// Owner style rules baked in here so every card inherits them: no capitals, no
// bright outlines (status is a soft tinted fill, never a coloured border), no
// border change on hover (hover tints the background), and separators are
// hairlines or whitespace, never dots.
import type { ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Panel } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons/names';
import { softControlMotion } from '@/theme/interactions';
import { IconTile, RowHint, RowLabel } from './dashStyles';
import type { CardTone } from './dashStyles';

const CardShell = styled(Panel)<{
  $span?: number;
  $spanMid?: number;
  $tone?: CardTone;
}>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  scroll-margin-top: 96px;
  grid-column: span ${({ $span }) => $span ?? 2};
  @container (max-width: 1059.98px) {
    grid-column: span ${({ $spanMid }) => $spanMid ?? 1};
  }
  @container (max-width: 619.98px) {
    grid-column: span 1;
    padding: 16px;
  }
  ${({ theme, $tone }) =>
    $tone === 'danger' &&
    css`
      background-color: color-mix(in srgb, ${theme.color.danger} 6%, ${theme.color.surface});
    `}
  ${({ theme, $tone }) =>
    $tone === 'accent' &&
    css`
      background-color: color-mix(in srgb, ${theme.color.accentCyan} 9%, ${theme.color.surface});
    `}
`;

export interface DashCardProps {
  /** id of the card's title, which names the <section>. */
  labelledBy: string;
  /** Columns out of 6 when wide (default 2). */
  span?: number;
  /** Columns out of 2 in the middle layout (default 1). */
  spanMid?: number;
  tone?: CardTone;
  id?: string;
  testId?: string;
  ref?: Ref<HTMLElement>;
  children: ReactNode;
}

export function DashCard({
  labelledBy,
  span,
  spanMid,
  tone,
  id,
  testId,
  ref,
  children,
}: DashCardProps) {
  return (
    <CardShell
      as="section"
      ref={ref}
      id={id}
      data-testid={testId}
      aria-labelledby={labelledBy}
      $span={span}
      $spanMid={spanMid}
      $tone={tone}
    >
      {children}
    </CardShell>
  );
}

const Head = styled.div<{ $divided: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  ${({ $divided, theme }) =>
    $divided &&
    css`
      padding-bottom: 14px;
      margin-bottom: 4px;
      border-bottom: 1px solid ${theme.color.glassBorder};
    `}
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.h3<{ $tone?: CardTone }>`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: ${({ theme }) => theme.lineHeight.md};
  font-weight: 600;
  color: ${({ theme, $tone }) =>
    $tone === 'danger' ? theme.color.danger : theme.color.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Aside = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export interface CardHeadProps {
  icon: IconName;
  title: string;
  /** id for the <h3>, so the card's <section> can be aria-labelledby it. */
  titleId: string;
  subtitle?: ReactNode;
  aside?: ReactNode;
  tone?: CardTone;
  /** Draw the hairline under the header (cards whose body is a list of rows). */
  divided?: boolean;
}

export function CardHead({
  icon,
  title,
  titleId,
  subtitle,
  aside,
  tone,
  divided = true,
}: CardHeadProps) {
  return (
    <Head $divided={divided}>
      <IconTile $tone={tone} aria-hidden="true">
        <Icon name={icon} size={20} />
      </IconTile>
      <HeadText>
        <Title id={titleId} $tone={tone}>
          {title}
        </Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </HeadText>
      {aside && <Aside>{aside}</Aside>}
    </Head>
  );
}

// ─── Rows ────────────────────────────────────────────────────────────────────

const rowLayout = css`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
  > svg,
  > [data-row-icon] {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const RowRoot = styled.div`
  ${rowLayout}
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RowEnd = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

export interface DashRowProps {
  icon: IconName;
  label: ReactNode;
  hint?: ReactNode;
  /** The trailing control(s): a button, a switch, a pill. */
  children?: ReactNode;
  testId?: string;
}

export function DashRow({ icon, label, hint, children, testId }: DashRowProps) {
  return (
    <RowRoot data-testid={testId}>
      <Icon name={icon} size={20} />
      <RowText>
        <RowLabel>{label}</RowLabel>
        {hint && <RowHint>{hint}</RowHint>}
      </RowText>
      {children ? <RowEnd>{children}</RowEnd> : <span />}
    </RowRoot>
  );
}

const LinkRowRoot = styled(Link)`
  ${rowLayout}
  ${softControlMotion}
  grid-template-columns: 20px minmax(0, 1fr) auto 16px;
  margin: 0 -8px;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: inherit;
  text-decoration: none;
  &:last-child {
    padding-bottom: 12px;
  }
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const LinkValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
`;

export interface DashLinkRowProps {
  to: string;
  icon: IconName;
  label: string;
  value?: ReactNode;
  testId?: string;
}

/** A row that opens another settings screen: label left, current value right, chevron. */
export function DashLinkRow({ to, icon, label, value, testId }: DashLinkRowProps) {
  return (
    <LinkRowRoot to={to} data-testid={testId}>
      <Icon name={icon} size={20} />
      <RowLabel>{label}</RowLabel>
      <LinkValue>{value}</LinkValue>
      <Icon name="chevron-right" size={16} />
    </LinkRowRoot>
  );
}

// ─── Status pill ─────────────────────────────────────────────────────────────

type PillTone = 'success' | 'accent' | 'neutral' | 'danger';

const PillRoot = styled.span<{ $tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme, $tone }) => pillInk(theme, $tone)};
  background: ${({ theme, $tone }) =>
    `color-mix(in srgb, ${pillInk(theme, $tone)} 14%, transparent)`};
`;

function pillInk(
  theme: { color: { success: string; accentCyan: string; textMuted: string; danger: string } },
  tone: PillTone,
): string {
  switch (tone) {
    case 'success':
      return theme.color.success;
    case 'accent':
      return theme.color.accentCyan;
    case 'danger':
      return theme.color.danger;
    default:
      return theme.color.textMuted;
  }
}

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
`;

export interface PillProps {
  tone?: PillTone;
  dot?: boolean;
  children: ReactNode;
}

/** A soft tinted status chip ("Verified", "Active now", "Coming soon"). Never outlined. */
export function Pill({ tone = 'neutral', dot = false, children }: PillProps) {
  return (
    <PillRoot $tone={tone}>
      {dot && <Dot aria-hidden="true" />}
      {children}
    </PillRoot>
  );
}

/** The honesty chip for anything that is not built yet. */
export function ComingSoon() {
  return <Pill>Coming soon</Pill>;
}
